import React, { memo, useCallback, useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Product } from 'types/graphql-overrides';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { mapToListItemProps } from 'src/theme/provider/commerce-components/bridge/bridge-map-product-card';

import { useProductCardData } from './use-product-card-data';
import { FullCardView } from './views/full-card-view';

type ProductListItemProps = {
  product: Product;
  productIndex: number;
  onView?: (product: Product, index: number) => void;
  onClick: (product: Product, index: number) => void;
  onAddToCart: (product: Product, index: number, option: string) => void;
  hideBorder?: boolean;
};

const DefaultListItemSentinel: React.FC<any> = () => null;

/**
 * Container for the list product card.
 * Owns data fetching and action binding; delegates rendering to FullCardView or a custom remote component.
 */
const FullCard = ({
  product,
  productIndex,
  onView,
  onClick,
  onAddToCart,
  hideBorder = false,
}: ProductListItemProps) => {
  const flags = useFlags();
  const isCustomProductCardEnabled = flags['growth.ecomm.custom-product-card.rollout'] ?? false;

  const { components } = useCommerceComponents();
  const ResolvedListItem = useMemo(
    () =>
      isCustomProductCardEnabled
        ? components.ProductCardListItem({ fallback: DefaultListItemSentinel })
        : DefaultListItemSentinel,
    [isCustomProductCardEnabled, components]
  );
  const hasCustomListItem = ResolvedListItem !== DefaultListItemSentinel;

  const data = useProductCardData({
    product,
    productIndex,
    onView,
  });

  const router = useRouter();

  const handleLinkClick = useCallback((): void => {
    onClick(product, productIndex);
  }, [onClick, product, productIndex]);

  /** Analytics + SPA navigation for custom/SDK cards that don't have access to Next.js <Link> */
  const handleSdkLinkClick = useCallback((): void => {
    onClick(product, productIndex);
    void router?.push(data.product.href);
  }, [onClick, product, productIndex, router, data.product.href]);

  const handleOptionClick = useCallback(
    (value: string): void => {
      onAddToCart(product, productIndex, value);
    },
    [onAddToCart, product, productIndex]
  );

  if (hasCustomListItem) {
    const sdkProps = mapToListItemProps(
      {
        product: data.product,
        image: data.image,
        badges: data.badges,
        pricing: data.pricing,
        options: data.options,
        productIndex: data.productIndex,
      },
      { onLinkClick: handleSdkLinkClick, onOptionClick: handleOptionClick }
    );
    return (
      <div ref={data.containerRef}>
        <ResolvedListItem {...sdkProps} />
      </div>
    );
  }

  return (
    <FullCardView
      product={data.product}
      image={data.image}
      badges={data.badges}
      options={data.options}
      layout={{ hideBorder }}
      onLinkClick={handleLinkClick}
      onOptionClick={handleOptionClick}
      containerRef={data.containerRef}
      productIndex={data.productIndex}
    />
  );
};

export default memo(FullCard);
